<template>
    <div
        ref="oddsComponent"
        class="oddBox"
        v-bind:class="{ _euroSiteStyle: isEuroSiteStyle }"
    >
        <slot name="oddsLabel" />

        <div
            v-if="oddsInfo"
            v-bind:class="[
                'odd',
                displaySectionGAClass,
                {
                    odd_rise: oddRise,
                    odd_drop: oddDrop,
                    odd_selected: isOddsSelected,
                    odd_locked: isOddsSuspended,
                    odd_multiple: isEuroSiteStyle && isMixParlayMode,
                    odd_live: isEuroSiteStyle && oddsInfo.isLive,
                },
            ]"
            v-bind:data-market-type="oddsMarketType"
            v-bind:data-match="gaDataMatch"
            v-bind:market-type-data-list="marketOrder"
            v-bind:match-card-data-list="matchInfo.matchOrder"
            data-testid="odds-cell"
            v-on:click="onClickOdds"
        >
            <div v-if="!isOddsSuspended" class="odd-point">
                <span>{{ toFixedDecimalPoint(point, 2) }}</span>
            </div>
            <div
                v-if="!isOddsSuspended"
                class="odd-value"
                v-bind:class="{ _negative: isOddsNegative, rtl: isReverseDisplayOddsEnabled }"
            >
                {{ strReverse(formatMinDecimalPlace(priceOption.price, minimumDecimalPlace), isReverseDisplayOddsEnabled) }}
            </div>
            <div v-if="isOddsSuspended" class="odd-locked">
                <SvgIcon iconName="systemIcon_Locked" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref, toRefs } from 'vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOdds, IPriceOption } from '@/interface/IOdds';
    import { DisplaySection } from '@/interface/enum';
    import useOddsDisplay from '@/libraries/composables/useOddsDisplay';
    import { useZone } from '@/libraries/composables/useZone';
    import { getMarketTypeName } from '@/libraries/oddsDisplay';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'Odds',
        components: {
            SvgIcon,
        },
        props: {
            matchInfo: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            oddsInfo: {
                type: Object as PropType<IOdds>,
                required: true,
            },
            priceOption: {
                type: Object as PropType<IPriceOption>,
                required: true,
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
            marketOrder: {
                type: Number,
                default: -1,
            },
            isEuroSiteStyle: {
                type: Boolean,
                default: false,
            },
        },
        setup(props) {
            const { matchInfo, oddsInfo, priceOption, displaySection } = toRefs(props);
            const oddsComponent = ref<HTMLElement | null>(null);

            const {
                oddRise,
                oddDrop,
                isOddsNegative,
                isOddsSuspended,
                isOddsSelected,
                point,
                minimumDecimalPlace,
                gaDataMatch,
                toFixedDecimalPoint,
                onClickOdds,
                formatMinDecimalPlace,
                strReverse,
                isReverseDisplayOddsEnabled,
            } = useOddsDisplay({
                matchInfo,
                oddsInfo,
                priceOption,
                displaySection,
                oddsComponentRef: oddsComponent,
            });

            const { isInEFootballZone, isInMultipleSportsZone, isInSportsCupZone, isInLeagueZone } = useZone();
            const { isMixParlayMode } = storeToRefs(useRootStore());
            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const oddsMarketType = computed(() => getMarketTypeName(oddsInfo.value.marketType));

            const displaySectionGAMap: Partial<Record<DisplaySection, string>> = {
                [DisplaySection.TournamentCenter_UpcomingMatches]: 'TC_Upcoming_MatchCard_Btn_Odds',
                [DisplaySection.TournamentCenter_Groups]: 'TC_Standing_Group_MatchCard_Btn_Odds',
                [DisplaySection.TournamentCenter_Knockout]: 'TC_Standing_Knockouts_MatchCard_Btn_Odds',
                [DisplaySection.TournamentCenter_Country]: 'TC_Nations_MatchCard_Btn_Odds',
            };
            const displaySectionGAClass = computed(() => displaySectionGAMap[displaySection.value] || 'JARVIS_MatchCard_Btn_Odds');

            return {
                oddRise,
                oddDrop,
                isOddsNegative,
                isOddsSuspended,
                isOddsSelected,
                point,
                minimumDecimalPlace,
                gaDataMatch,
                isInLeagueZone,
                isReverseDisplayOddsEnabled,
                toFixedDecimalPoint,
                formatMinDecimalPlace,
                isInEFootballZone,
                isInMultipleSportsZone,
                isInSportsCupZone,
                onClickOdds,
                oddsMarketType,
                isComponentInPp,
                isMixParlayMode,
                strReverse,
                oddsComponent,
                displaySectionGAClass,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/odd";
</style>
